import { useEffect, useState } from "react";
import { Bell, Share, PlusSquare, X } from "lucide-react";

const OFF_KEY = "ios_notify_reminder_off";
const SNOOZE_KEY = "ios_notify_reminder_until";
const SNOOZE_DAYS = 3;

function isStandalone() {
  return (window.matchMedia?.("(display-mode: standalone)").matches) || window.navigator.standalone === true;
}
function isIOS() {
  const ua = window.navigator.userAgent || "";
  return /iphone|ipad|ipod/i.test(ua);
}

// Promemoria automatico su iPhone (app non installata): per ricevere le notifiche
// l'app va aggiunta alla schermata Home. Mostrato in modo discreto e ricordabile.
export function IosNotifyReminder() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (!isIOS() || isStandalone()) return;
    if (localStorage.getItem(OFF_KEY) === "1") return;
    const until = parseInt(localStorage.getItem(SNOOZE_KEY) || "0", 10);
    if (until && Date.now() < until) return;
    const t = setTimeout(() => setShow(true), 1200);
    return () => clearTimeout(t);
  }, []);

  if (!show) return null;

  const later = () => {
    localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_DAYS * 86400000));
    setShow(false);
  };
  const never = () => {
    localStorage.setItem(OFF_KEY, "1");
    setShow(false);
  };

  return (
    <div data-testid="ios-notify-reminder"
      className="fixed inset-x-3 bottom-24 md:bottom-6 md:left-auto md:right-6 md:max-w-sm z-50 rounded-2xl border-2 border-[#EBC5D2] bg-white shadow-2xl p-4 animate-in slide-in-from-bottom-4">
      <button data-testid="ios-notify-close" onClick={later} aria-label="Chiudi"
        className="absolute top-2 right-2 p-1 text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
      <div className="flex items-start gap-3">
        <div className="bg-brand text-white rounded-xl p-2 shrink-0"><Bell className="w-6 h-6" /></div>
        <div className="min-w-0">
          <h3 className="font-display font-bold text-base text-slate-800">Attiva le notifiche su iPhone</h3>
          <p className="text-sm text-slate-600 mt-0.5">
            Per ricevere gli avvisi sul telefono devi prima <b>aggiungere Ginius alla schermata Home</b> e aprirla da lì.
          </p>
        </div>
      </div>
      <ol className="space-y-2 text-sm text-slate-700 mt-3 bg-[#FBEEF3]/60 rounded-lg p-3">
        <li className="flex gap-2"><Share className="w-4 h-4 mt-0.5 text-brand shrink-0" /> In <b>Safari</b>, tocca il pulsante <b>Condividi</b> (quadrato con la freccia in alto).</li>
        <li className="flex gap-2"><PlusSquare className="w-4 h-4 mt-0.5 text-brand shrink-0" /> Scegli <b>“Aggiungi a Home”</b> e conferma.</li>
        <li className="flex gap-2"><Bell className="w-4 h-4 mt-0.5 text-brand shrink-0" /> Apri l'app dalla Home e tocca <b>“Attiva avvisi sul telefono”</b> nella campanella.</li>
      </ol>
      <div className="flex items-center justify-between gap-2 mt-3">
        <button data-testid="ios-notify-never" onClick={never} className="text-xs text-slate-400 hover:text-slate-600 underline">Non mostrare più</button>
        <button data-testid="ios-notify-later" onClick={later} className="h-9 px-4 rounded-lg bg-brand text-white text-sm font-bold hover:bg-brand-dark">Ho capito</button>
      </div>
    </div>
  );
}
